import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, ArrowRight, Search, Tag } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * BlogPage component - Thought leadership and content showcase
 * Author: VB Entreprise
 * 
 * Features:
 * - Blog post grid with categories
 * - Search and filtering functionality
 * - Featured articles
 * - Resource library
 */
const BlogPage: React.FC = () => {
  const featuredPost = {
    id: 1,
    title: 'The Future of Enterprise Software Development: Trends to Watch in 2024',
    excerpt: 'Explore the latest trends shaping enterprise software development, from AI integration to microservices architecture and cloud-native solutions.',
    author: 'Vincent Beaumont',
    date: '2024-01-15',
    readTime: '8 min read',
    category: 'Technology',
    image: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=800',
    featured: true
  };

  const blogPosts = [
    {
      id: 2,
      title: 'Cloud Migration Strategy: A Complete Guide for Enterprises',
      excerpt: 'Learn how to plan and execute a successful cloud migration with minimal downtime and maximum ROI.',
      author: 'Sarah Mitchell',
      date: '2024-01-12',
      readTime: '12 min read',
      category: 'Cloud',
      image: 'https://images.pexels.com/photos/1181671/pexels-photo-1181671.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      id: 3,
      title: 'Digital Marketing ROI: Measuring Success in 2024',
      excerpt: 'Discover the key metrics and strategies for maximizing your digital marketing return on investment.',
      author: 'Emily Chen',
      date: '2024-01-10',
      readTime: '6 min read',
      category: 'Marketing',
      image: 'https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      id: 4,
      title: 'Cybersecurity Best Practices for Remote Teams',
      excerpt: 'Essential security measures every organization should implement for their distributed workforce.',
      author: 'Marcus Johnson',
      date: '2024-01-08',
      readTime: '10 min read',
      category: 'Security',
      image: 'https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      id: 5,
      title: 'API-First Development: Building Scalable Systems',
      excerpt: 'Why API-first architecture is crucial for modern software development and how to implement it effectively.',
      author: 'Sarah Mitchell',
      date: '2024-01-05',
      readTime: '9 min read',
      category: 'Development',
      image: 'https://images.pexels.com/photos/1181671/pexels-photo-1181671.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      id: 6,
      title: 'E-commerce Platform Selection: A Buyer\'s Guide',
      excerpt: 'Compare the top e-commerce platforms and find the perfect solution for your business needs.',
      author: 'Emily Chen',
      date: '2024-01-03',
      readTime: '7 min read',
      category: 'E-commerce',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=400'
    },
    {
      id: 7,
      title: 'Artificial Intelligence in Business: Practical Applications',
      excerpt: 'Real-world AI use cases that are transforming businesses across industries.',
      author: 'Vincent Beaumont',
      date: '2024-01-01',
      readTime: '11 min read',
      category: 'AI/ML',
      image: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=400'
    }
  ];

  const categories = [
    'All',
    'Technology',
    'Cloud',
    'Marketing',
    'Security',
    'Development',
    'E-commerce',
    'AI/ML'
  ];

  const resources = [
    {
      title: 'Digital Transformation Checklist',
      description: 'A comprehensive guide to planning your digital transformation journey.',
      type: 'Checklist',
      downloadUrl: '#'
    },
    {
      title: 'Cloud Migration ROI Calculator',
      description: 'Calculate the potential savings and ROI of migrating to the cloud.',
      type: 'Tool',
      downloadUrl: '#'
    },
    {
      title: 'Cybersecurity Assessment Framework',
      description: 'Evaluate your organization\'s security posture with our assessment framework.',
      type: 'Framework',
      downloadUrl: '#'
    },
    {
      title: 'API Design Best Practices Guide',
      description: 'Learn how to design APIs that are scalable, secure, and developer-friendly.',
      type: 'Guide',
      downloadUrl: '#'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Blog | VB Entreprise | Premium IT & Digital Marketing Agency for US, UK & Canada Businesses</title>
      </Helmet>
      <div className="pt-8">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Insights & <span className="text-blue-600">Resources</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Stay ahead of the curve with expert insights, industry trends, and practical guides from our technology leaders.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-md mx-auto relative">
              <input
                type="text"
                placeholder="Search articles..."
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-blue-100 hover:text-blue-700 text-gray-600"
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Article */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl overflow-hidden text-white">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="inline-block bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                    Featured Article
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                    {featuredPost.title}
                  </h2>
                  <p className="text-blue-100 mb-6 text-lg">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center space-x-6 text-blue-100 text-sm mb-6">
                    <div className="flex items-center space-x-2">
                      <User size={16} />
                      <span>{featuredPost.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock size={16} />
                      <span>{featuredPost.readTime}</span>
                    </div>
                  </div>
                  <Link
                    to={`/blog/${featuredPost.id}`}
                    className="inline-flex items-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  >
                    <span>Read Article</span>
                    <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="lg:block hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Latest Articles</h2>
              <p className="text-xl text-gray-600">
                Expert insights and practical guides from our team.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                      <Link to={`/blog/${post.id}`}>{post.title}</Link>
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <User size={14} />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-12">
              <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Load More Articles
              </button>
            </div>
          </div>
        </section>

        {/* Resource Library */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Resource Library</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Download our free resources to help accelerate your technology initiatives.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {resources.map((resource, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium mb-3">
                        {resource.type}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{resource.title}</h3>
                      <p className="text-gray-600 mb-4">{resource.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Stay Informed</h2>
            <p className="text-xl text-blue-100 mb-8">
              Subscribe to our newsletter for the latest insights, industry trends, and exclusive resources.
            </p>
            <form className="flex flex-col sm:flex-row max-w-md mx-auto space-y-4 sm:space-y-0 sm:space-x-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-300 focus:outline-none"
              />
              <button
                type="submit"
                className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                Subscribe
              </button>
            </form>
            <p className="text-blue-200 text-sm mt-4">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPage;